import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Stack,
  useToast,
} from '@chakra-ui/react';
import AnnouncementResource from 'api/announcement';
import AnnouncementForm from 'components/announcement/AnnouncementForm';
import { strings } from 'config/localization';
import routes from 'constants/routes';
import { AnnouncementSchema } from 'constants/schema';
import React, { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { shallowEqual, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { buildFormData } from 'utils';
import { getFullName } from 'utils/esg';

const CreateAnnouncement: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useRef() as React.MutableRefObject<HTMLFormElement>;
  const history = useHistory();
  const methods = useForm<AnnouncementSchema>();
  const announcementAPI = new AnnouncementResource();

  const [errorMessage, setErrorMessage] = useState<AnnouncementSchema>();

  const { loggedInUser } = useSelector(
    (state: any) => ({
      loggedInUser: state?.data?.auth?.user,
    }),
    shallowEqual
  );

  const addAnnouncement = useMutation(
    (data: any) => announcementAPI.store(data),
    {
      onSuccess: (res: any) => {
        toast({
          title: strings.announcement_add_msg,
          description: res.data.email,
          status: 'success',
          isClosable: true,
        });
        queryClient.invalidateQueries('announcementList');
        history.push(routes.announcements.list);
      },
      onError: (error: any) => {
        setErrorMessage(error?.response?.data?.errors);
      },
    }
  );

  const onSubmit = (data: any) => {
    data.written_by = getFullName(loggedInUser?.user_information);
    const formData = buildFormData(data);
    addAnnouncement.mutate(formData);
  };

  return (
    <Stack direction="column" spacing="4">
      <Breadcrumb color="gray.400" size="4">
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to={routes.announcements.list}>
            {strings.announcement_management}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage color="gray.900">
          <BreadcrumbLink as={RouterLink} to={routes.announcements.create}>
            {strings.add_new_announcement}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex justify="space-between">
        <Heading size="md">{strings.add_new_announcement}</Heading>
      </Flex>

      <Box bg="white" p={['3', '6']} shadow="box" rounded="sm">
        <FormProvider {...methods}>
          <form ref={form} onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack direction="column" spacing="4">
              {addAnnouncement.isError && (
                <Alert status="error">
                  <AlertIcon />
                  {strings.announcement_creation_failed}
                </Alert>
              )}
              <AnnouncementForm />
              <ButtonGroup>
                <Button
                  colorScheme="primary"
                  type="submit"
                  isLoading={addAnnouncement.isLoading}>
                  {strings.add_announcement}
                </Button>
                <Button variant="outline" onClick={() => history.goBack()}>
                  {strings.cancel}
                </Button>
              </ButtonGroup>
            </Stack>
          </form>
        </FormProvider>
      </Box>
    </Stack>
  );
};

export default CreateAnnouncement;
